import type { LocalizedText } from '#contract'
import { AppFrame } from './app-frame.tsx'
import { appearanceAt, useOsClock } from './clock.ts'
import { Launcher } from './launcher.tsx'
import type { OsMenuItem } from './menu-bar-menu.tsx'
import styles from './os.module.css'
import type { OsState } from './os-state.ts'
import { StatusBar } from './status-bar.tsx'
import './os-theme.css'

/** A key and what it does, shown at the right of the desktop's menu bar ("Esc Stand up"). */
export interface OsMenuHint {
  readonly key: string
  readonly label: LocalizedText
}

interface OsProps {
  readonly state: OsState
  /** The system (logo) menu's items, from the host: the computer adds "Stand up". */
  readonly systemItems?: readonly OsMenuItem[]
  readonly menuHint?: OsMenuHint | null
}

const NO_ITEMS: readonly OsMenuItem[] = []
const FOCUSABLE = 'button:not([disabled]):not([tabindex="-1"])'

/**
 * The in-game OS: one tree for the phone and the desktop, told apart by `data-form` (layout
 * and look) and `data-appearance` (dark from 20:00 to 07:00 of the game clock). The host
 * renders it inside a size container: the phone's screen or the computer's.
 */
export function Os({ state, systemItems = NO_ITEMS, menuHint = null }: OsProps) {
  const minutes = useOsClock()
  // The gamepad's left and right arrive as arrow keys on the focused button (as from a
  // keyboard): they step through the OS's buttons in DOM order.
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    const root = event.currentTarget as HTMLElement
    const targets = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (target) => target.closest('[inert]') === null,
    )
    const index = targets.indexOf(event.target as HTMLElement)
    if (index < 0) return
    const step = event.key === 'ArrowRight' ? 1 : -1
    targets.at((index + step) % targets.length)?.focus({ focusVisible: true })
    event.preventDefault()
  }

  return (
    <div
      class={styles.os}
      data-os=""
      data-form={state.form}
      data-appearance={appearanceAt(minutes)}
      onKeyDown={onKeyDown}
    >
      <Launcher state={state} />
      <Windows state={state} />
      <StatusBar state={state} minutes={minutes} systemItems={systemItems} hint={menuHint} />
    </div>
  )
}

/** The phone shows its front app alone; the desktop every window. */
function Windows({ state }: { readonly state: OsState }) {
  const windows = state.windows.value
  const front = state.front.value
  const shown = state.form === 'phone' ? windows.filter((entry) => entry.app === front) : windows
  return (
    <div class={styles.windows}>
      {shown.map((entry) => (
        <AppFrame key={entry.app} state={state} entry={entry} />
      ))}
    </div>
  )
}
